"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { createClient } from "@/lib/supabase/client"
import { useAuthStore } from "@/lib/auth-store"
import { useDraftStore } from "@/lib/draft-store"
import { Loader2, Check, X, Eye, EyeOff } from "@/lib/icons"

const LAST_LOGIN_METHOD_KEY = "goonru-last-login-method"
type LoginMethod = "google" | "email"

function rememberLoginMethod(method: LoginMethod) {
  try { localStorage.setItem(LAST_LOGIN_METHOD_KEY, method) } catch {}
}

// Goop slime frame (public/borders/goopborder-slime-portrait-light.webp, a 1.1x brighter, 90% opacity copy of goopborder-slime-portrait.webp; 941px wide, art is ~46px thick). The 120px corner slices
// are drawn at a third of their size (40px, so the art is ~15px thick and the edges stretch without seams), so any dialog
// height works without distortion. The rest of the border area is transparent, hence the 16px padding.
const GOOP_FRAME: React.CSSProperties = {
  border: "1px solid transparent",
  borderImage: "url(/borders/goopborder-slime-portrait-light.webp) 120 / 40px stretch",
  // Draw the frame 4px outside the card so the card edge and its corners never peek out past it.
  borderImageOutset: 4,
  borderRadius: 18,
  padding: 16,
}

interface LoginPromptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  feature?: string
  onSuccess?: () => void
  initialMode?: "login" | "signup" | "forgot-password"
}

export function LoginPromptDialog({ open, onOpenChange, feature = "this feature", onSuccess, initialMode }: LoginPromptDialogProps) {
  const router = useRouter()
  const t = useTranslations("auth")
  const [mode, setMode] = useState<"login" | "signup" | "confirm-email" | "forgot-password" | "reset-sent">(initialMode ?? "login")

  useEffect(() => { if (open && initialMode) setMode(initialMode) }, [open, initialMode])
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [username, setUsername] = useState("")
  const [displayName, setDisplayName] = useState("")
  // Date of birth (owner decision 2026-10-29): required at signup, 18+ decided on the server, stored privately after signup.
  const [birthDate, setBirthDate] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [usernameStatus, setUsernameStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle")
  const [usernameError, setUsernameError] = useState("")
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const refreshProfile = useAuthStore((state) => state.refreshProfile)
  const migrateGuestDrafts = useDraftStore((state) => state.migrateGuestDrafts)
  const [lastMethod, setLastMethod] = useState<LoginMethod | null>(null)

  // Read after mount (not in the initial state) so server and client render the same HTML.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LAST_LOGIN_METHOD_KEY)
      if (saved === "google" || saved === "email") setLastMethod(saved)
    } catch {}
  }, [open])

  // Real-time username validation
  useEffect(() => {
    if (mode !== "signup" || !username) {
      setUsernameStatus("idle")
      setUsernameError("")
      return
    }

    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/
    if (!usernameRegex.test(username)) {
      setUsernameStatus("invalid")
      setUsernameError(t("usernameInvalid"))
      return
    }

    setUsernameStatus("checking")
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(`/api/auth/check-username?username=${encodeURIComponent(username)}`)
        const data = await response.json()
        
        if (data.available) {
          setUsernameStatus("available")
          setUsernameError("")
        } else if (data.blocked) {
          setUsernameStatus("invalid")
          setUsernameError(t("nameNotAllowed"))
        } else {
          setUsernameStatus("taken")
          setUsernameError(t("usernameTaken"))
        }
      } catch (error) {
        console.error("Username check error:", error)
        setUsernameStatus("idle")
      }
    }, 500) // Debounce 500ms

    return () => clearTimeout(timeoutId)
  }, [username, mode])

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    try {
      const supabase = createClient()
      // Route through the server-side callback so the code exchange happens
      // server-side (cookies) instead of client-side (localStorage).
      // This means the reset link works even if opened in a different browser/app.
      const redirectTo = `${window.location.origin}/auth/callback?next=/auth/reset-password`
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
      if (error) throw error
      setMode("reset-sent")
    } catch (err: any) {
      const message = err.message || t("errorResetEmailFailed")
      setError(
        message.toLowerCase().includes("redirect")
          ? `${message}. If you are testing locally, add ${window.location.origin}/auth/reset-password to Supabase Auth redirect URLs.`
          : message,
      )
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setEmail("")
    setPassword("")
    setConfirmPassword("")
    setUsername("")
    setDisplayName("")
    setError("")
    setUsernameStatus("idle")
    setUsernameError("")
    setTurnstileToken(null)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    try {
      const supabase = createClient()
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        const msg = error.message.toLowerCase()
        if (msg.includes("email not confirmed") || msg.includes("email_not_confirmed")) {
          // Auto-resend confirmation so they can act immediately
          await supabase.auth.resend({ type: "signup", email })
          throw new Error(t("errorEmailNotConfirmed"))
        }
        throw new Error(msg.includes("invalid login credentials") ? t("errorInvalidCredentials") : error.message)
      }
      if (data.user) {
        rememberLoginMethod("email")
        await refreshProfile(data.user)
        migrateGuestDrafts().catch(() => {})
      }
      onOpenChange(false)
      if (onSuccess) onSuccess()
      else router.refresh()
    } catch (err: any) {
      setError(err.message || t("errorLoginFailed"))
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate username before submitting
    if (usernameStatus !== "available") {
      setError(t("errorChooseValidUsername"))
      return
    }

    // The display name can be anything, so it gets the same blocked-word check as the username
    if (displayName.trim()) {
      try {
        const res = await fetch(`/api/auth/check-username?username=${encodeURIComponent(username)}&displayName=${encodeURIComponent(displayName)}`)
        const data = await res.json()
        if (data?.blocked) {
          setError(t("nameNotAllowed"))
          return
        }
      } catch {}
    }

    // Date of birth: required, and 18+ is decided by the server's clock before any account is created
    if (!birthDate) {
      setError(t("birthDateRequired"))
      return
    }
    try {
      const res = await fetch("/api/auth/check-signup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ birthDate }) })
      const data = await res.json()
      if (!data?.ok) {
        setError(data?.code === "too_young" ? t("mustBe18") : t("birthDateInvalid"))
        return
      }
    } catch {
      setError(t("birthDateInvalid"))
      return
    }

    // Validate password match
    if (password !== confirmPassword) {
      setError(t("passwordsDoNotMatch"))
      return
    }

    // Validate password strength
    if (password.length < 8) {
      setError(t("errorPasswordTooShort"))
      return
    }

    // Validate Turnstile (bot protection)
    if (!turnstileToken) {
      setError(t("errorCompleteVerification"))
      return
    }

    setLoading(true)
    setError("")
    try {
      // Real server-side check — the token was previously only confirmed to
      // exist (a bot can send any string), never confirmed to be REAL via
      // Cloudflare. See app/api/auth/verify-turnstile.
      const verifyRes = await fetch("/api/auth/verify-turnstile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: turnstileToken }),
      })
      const verifyData = await verifyRes.json()
      if (!verifyRes.ok || !verifyData.success) {
        setError(t("errorCompleteVerification"))
        setLoading(false)
        return
      }

      const supabase = createClient()
      
      // Sign up with auto-confirm (no email verification required)
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { 
            username,
            display_name: displayName || username,
            turnstile_token: turnstileToken,
          },
          emailRedirectTo: window.location.hostname === "localhost" ? window.location.origin : process.env.NEXT_PUBLIC_DEV_SUPABASE_REDIRECT_URL || window.location.origin,
        },
      })
      
      if (signupError) throw signupError
      
      if (signupData.user) {
        const signedUpUser = signupData.user
        // Save the date of birth. No cookies exist yet after signup, so the new session's access token goes in a header.
        const newAccessToken = signupData.session?.access_token
        if (newAccessToken) {
          fetch("/api/account/birth-date", {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${newAccessToken}` },
            body: JSON.stringify({ birthDate }),
          }).catch(() => {})
        }
        // Mark as logged in immediately so the UI reflects the new session
        useAuthStore.getState().setUser(signedUpUser)
        onOpenChange(false)
        if (onSuccess) onSuccess()
        else router.refresh()
        // Migrate any guest drafts to the new account (non-blocking)
        migrateGuestDrafts().catch(() => {})
        // The server-side profile API needs cookies which aren't set yet after signup
        // (flowType=implicit stores session in localStorage only). Query the profile
        // directly from the client Supabase — it already has the session in memory —
        // and retry because the handle_new_user trigger may not have run yet.
        const supabaseClient = createClient()
        ;(async () => {
          for (let attempt = 0; attempt < 5; attempt++) {
            await new Promise((r) => setTimeout(r, 600 * (attempt + 1)))
            try {
              const { data: profileRow } = await supabaseClient
                .from("profiles")
                .select("*")
                .eq("id", signedUpUser.id)
                .single()
              if (profileRow) {
                useAuthStore.getState().setProfile(profileRow)
                break
              }
            } catch {
              // trigger hasn't created the row yet — keep retrying
            }
          }
        })()
      }
    } catch (err: any) {
      setError(err.message.includes("already registered") ? t("errorAlreadyRegistered") : err.message || t("errorSignupFailed"))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setLoading(true)
    setError("")
    // Recorded the instant the button is pressed: the page navigates away to
    // Google shortly after, and the slower steps below (server session clear,
    // sign-out) must not be able to delay or lose it. Not one of the keys
    // cleared below, so it survives the clean-slate sign-out.
    rememberLoginMethod("google")
    try {
      console.log('[Google OAuth] Step 1: Clearing all auth state')
      
      // CRITICAL: Clear ALL auth state before OAuth to prevent account switching
      // Clear client-side localStorage
      if (typeof window !== "undefined") {
        localStorage.removeItem("goonru-auth-storage")
        localStorage.removeItem("goonru-auth")
        // Clear any legacy Supabase keys
        Object.keys(localStorage)
          .filter((k) => k.startsWith("sb-"))
          .forEach((k) => localStorage.removeItem(k))
      }
      
      // Clear server-side session and cookies
      try {
        console.log('[Google OAuth] Step 2: Calling server-side session clear')
        await fetch("/api/auth/clear-session", { method: "POST" })
      } catch (e) {
        console.warn('[Google OAuth] Server-side clear failed (non-blocking):', e)
      }
      
      // Also sign out from client
      const supabase = createClient()
      await supabase.auth.signOut()
      
      console.log('[Google OAuth] Step 3: Initiating OAuth flow')
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { 
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        },
      })
      if (error) throw error
    } catch (err: any) {
      setError(err.message || t("errorGoogleLoginFailed"))
      setLoading(false)
    }
  }

  // Load Turnstile script and set up callback
  useEffect(() => {
    if (mode !== "signup") return

    // Define global callback for Turnstile
    (window as any).onTurnstileSuccess = (token: string) => {
      setTurnstileToken(token)
    }

    // Load Turnstile script if not already loaded
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }

    return () => {
      delete (window as any).onTurnstileSuccess
    }
  }, [mode])

  // Forgot password screen
  if (mode === "forgot-password") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm overflow-hidden bg-background/95 backdrop-blur-2xl shadow-2xl" style={GOOP_FRAME}>
          <div className="p-8 space-y-4">
            <div className="text-center space-y-1">
              <h2 className="text-xl font-bold">{t("resetPasswordTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("resetPasswordSubtitle")}</p>
            </div>
            <form onSubmit={handleForgotPassword} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("email")}</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("emailPlaceholder")}
                  required
                  autoFocus
                />
              </div>
              {error && (
                <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
              )}
              <Button type="submit" className="w-full" disabled={loading || !email}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("sendResetLink")}
              </Button>
            </form>
            <div className="text-center">
              <span
                onClick={() => { setError(""); setMode("login") }}
                className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              >
                {t("backToSignIn")}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Reset email sent screen
  if (mode === "reset-sent") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm overflow-hidden bg-background/95 backdrop-blur-2xl shadow-2xl" style={GOOP_FRAME}>
          <div className="p-8 space-y-4 text-center">
            <div className="w-12 h-12 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto text-2xl">
              📬
            </div>
            <div>
              <h2 className="text-xl font-bold">{t("checkYourEmail")}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t.rich("resetLinkSentTo", {
                  email,
                  em: (chunks) => <span className="text-foreground font-medium">{chunks}</span>,
                })}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("resetLinkExpiry")}
            </p>
            {typeof window !== "undefined" && window.location.hostname === "localhost" && (
              <p className="text-[11px] text-muted-foreground">
                {t("localhostResetNote")}
              </p>
            )}
            <div className="flex flex-col gap-2 pt-2">
              <Button className="w-full" onClick={() => { resetForm(); setMode("login") }}>
                {t("backToSignIn")}
              </Button>
              <Button variant="ghost" className="w-full text-sm text-muted-foreground" onClick={() => { setError(""); setMode("forgot-password") }}>
                {t("resendEmail")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Confirm email screen
  if (mode === "confirm-email") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm overflow-hidden bg-background/95 backdrop-blur-2xl shadow-2xl" style={GOOP_FRAME}>
          <div className="p-8 space-y-4 text-center">
            <div className="w-12 h-12 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto text-2xl">
              📬
            </div>
            <div>
              <h2 className="text-xl font-bold">{t("checkYourEmail")}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t.rich("confirmationSentTo", {
                  email,
                  em: (chunks) => <span className="text-foreground font-medium">{chunks}</span>,
                })}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("confirmThenLogIn")}
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button className="w-full" onClick={() => { resetForm(); setMode("login") }}>
                {t("goToLogin")}
              </Button>
              <Button variant="ghost" className="w-full text-sm text-muted-foreground" onClick={() => { resetForm(); setMode("signup") }}>
                {t("tryDifferentEmail")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className={`${mode === "signup" ? "sm:max-w-[30rem]" : "sm:max-w-sm"} p-0 overflow-hidden rounded-2xl shadow-2xl`}
        style={{ ...GOOP_FRAME, backgroundColor: "var(--login-card)" } as React.CSSProperties}
      >
        {/* Hidden title for accessibility */}
        <DialogTitle className="sr-only">
          {mode === "login" ? t("signInTitle") : mode === "signup" ? t("signUpTitle") : t("confirmEmailTitle")}
        </DialogTitle>
        
        {/* CSS vars per theme */}
        <style>{`
          :root {
            --login-card: #ffffff;
            --login-border: #e5e5e5;
            --login-heading: #000000;
            --login-label: #333333;
            --login-secondary: #666666;
            --login-input-bg: #ffffff;
            --login-input-border: #d9d9d9;
            --login-google: #6f4deb;
            --login-signin: #1f1f1f;
            --login-signin-text: #ffffff;
            --login-signup-link: #6f4deb;
            --login-guest-bg: #f5f5f5;
            --login-guest-border: #d9d9d9;
            --login-guest-text: #666666;
            --login-divider: #e5e5e5;
            --login-divider-text: #999999;
          }
          html.dark {
            --login-card: #1a1a1a;
            --login-border: #3a3a3a;
            --login-heading: #ffffff;
            --login-label: #e0e0e0;
            --login-secondary: #b0b0b0;
            --login-input-bg: #111111;
            --login-input-border: #3a3a3a;
            --login-google: #6f4deb;
            --login-signin: #4a8aff;
            --login-signin-text: #ffffff;
            --login-signup-link: #a493ff;
            --login-guest-bg: #222222;
            --login-guest-border: #3a3a3a;
            --login-guest-text: #b0b0b0;
            --login-divider: #3a3a3a;
            --login-divider-text: #666666;
          }
          html.auto {
            --login-card: #191d27;
            --login-border: #2a2e3a;
            --login-heading: #ffffff;
            --login-label: #e0e2e8;
            --login-secondary: #a0a4b0;
            --login-input-bg: #151821;
            --login-input-border: #2a2e3a;
            --login-google: #6f4deb;
            --login-signin: #5b7bff;
            --login-signin-text: #ffffff;
            --login-signup-link: #a493ff;
            --login-guest-bg: #252933;
            --login-guest-border: #2a2e3a;
            --login-guest-text: #a0a4b0;
            --login-divider: #2a2e3a;
            --login-divider-text: #555a6a;
          }
        `}</style>

        {/* Header */}
        <div className="px-5 pt-5 pb-4 relative" style={{ borderBottom: "1px solid var(--login-border)" }}>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 transition-opacity hover:opacity-70"
            style={{ color: "var(--login-secondary)" }}
          >
            ✕
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-center" style={{ color: "var(--login-heading)" }}>{t("goonru")}</h1>
          <p className="text-sm mt-1.5 text-center" style={{ color: "var(--login-secondary)" }}>
            {mode === "login" ? t("welcomeBack") : t("createFreeAccount")}
          </p>
        </div>

        {/* Body */}
        <div className="px-5 pt-3 pb-2 space-y-3">

          {/* Google button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="relative w-full h-10 rounded-lg font-semibold flex items-center justify-center gap-2.5 transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: "var(--login-google)", color: "#ffffff" }}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#ffffff" }} /> : (
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24">
                <path fill="#ffffff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#ffffff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#ffffff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#ffffff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            <span style={{ color: "#ffffff" }}>{t("continueWithGoogle")}</span>
            {lastMethod === "google" && mode === "login" && (
              <span className="absolute -top-2 right-3 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none" style={{ backgroundColor: "var(--login-card)", color: "var(--login-secondary)", border: "1px solid var(--login-border)" }}>
                {t("lastUsed")}
              </span>
            )}
          </button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" style={{ borderColor: "var(--login-divider)" }} />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 uppercase tracking-widest" style={{ backgroundColor: "var(--login-card)", color: "var(--login-divider-text)" }}>{t("or")}</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={mode === "login" ? handleLogin : handleSignup} className="space-y-2.5">
            {mode === "signup" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--login-label)" }}>{t("username")}</label>
                  <div className="relative">
                    <input
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase())}
                      placeholder={t("usernamePlaceholder")}
                      required
                      minLength={3}
                      maxLength={20}
                      pattern="[a-zA-Z0-9_]+"
                      className="w-full h-9 rounded-lg px-3 pr-10 text-sm outline-none transition-colors"
                      style={{ 
                        backgroundColor: "var(--login-input-bg)", 
                        border: `1px solid ${usernameStatus === "available" ? "#22c55e" : usernameStatus === "taken" || usernameStatus === "invalid" ? "#ef4444" : "var(--login-input-border)"}`, 
                        color: "var(--login-heading)" 
                      }}
                    />
                    {usernameStatus === "checking" && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {usernameStatus === "available" && (
                      <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    )}
                    {(usernameStatus === "taken" || usernameStatus === "invalid") && (
                      <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                    )}
                  </div>
                  {usernameError && (
                    <p className="text-xs text-red-500">{usernameError}</p>
                  )}
                  {usernameStatus === "available" && (
                    <p className="text-xs text-green-600">{t("usernameAvailable")}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--login-label)" }}>
                    {t("displayName")}
                  </label>
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={`${t("displayNamePlaceholder")} ${t("optional")}`}
                    maxLength={30}
                    className="w-full h-9 rounded-lg px-3 text-sm outline-none transition-colors"
                    style={{ backgroundColor: "var(--login-input-bg)", border: "1px solid var(--login-input-border)", color: "var(--login-heading)" }}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--login-label)" }}>
                    {t("dateOfBirth")}
                  </label>
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    min="1900-01-01"
                    max={new Date().toISOString().slice(0, 10)}
                    required
                    className="w-full h-9 rounded-lg px-3 text-sm outline-none transition-colors"
                    style={{ backgroundColor: "var(--login-input-bg)", border: "1px solid var(--login-input-border)", color: "var(--login-heading)" }}
                  />
                  <p className="text-[11px]" style={{ color: "var(--login-label)" }}>{t("birthDateHint")}</p>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--login-label)" }}>{t("email")}</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("emailPlaceholder")} required
                className="w-full h-9 rounded-lg px-3 text-sm outline-none transition-colors"
                style={{ backgroundColor: "var(--login-input-bg)", border: "1px solid var(--login-input-border)", color: "var(--login-heading)" }}
              />
            </div>
            <div className={mode === "signup" ? "grid grid-cols-1 sm:grid-cols-2 gap-2.5" : undefined}>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--login-label)" }}>{t("password")}</label>
                {mode === "login" && (
                  <span
                    onClick={() => { setError(""); setMode("forgot-password") }}
                    className="text-xs cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ color: "var(--login-signup-link)" }}
                  >
                    {t("forgotPassword")}
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  placeholder={mode === "signup" ? t("passwordMinHint") : "••••••••"} 
                  minLength={8} 
                  required
                  className="w-full h-9 rounded-lg px-3 pr-10 text-sm outline-none transition-colors"
                  style={{ backgroundColor: "var(--login-input-bg)", border: "1px solid var(--login-input-border)", color: "var(--login-heading)" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {mode === "signup" && (
              <div className="space-y-1">
                <label className="block text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--login-label)" }}>{t("confirmPassword")}</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                    placeholder="••••••••" 
                    minLength={8} 
                    required
                    className="w-full h-9 rounded-lg px-3 pr-10 text-sm outline-none transition-colors"
                    style={{ 
                      backgroundColor: "var(--login-input-bg)", 
                      border: `1px solid ${password && confirmPassword && password !== confirmPassword ? "#ef4444" : "var(--login-input-border)"}`, 
                      color: "var(--login-heading)" 
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-500">{t("passwordsDoNotMatch")}</p>
                )}
              </div>
            )}
            </div>

            {mode === "signup" && (
              <div className="space-y-1">
                <div 
                  className="cf-turnstile" 
                  data-sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                  data-callback="onTurnstileSuccess"
                  data-theme="auto"
                ></div>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit" disabled={loading}
              className="relative w-full h-10 rounded-lg font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "var(--login-signin)", color: "var(--login-signin-text)" }}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? t("signIn") : t("createAccount")}
              {lastMethod === "email" && mode === "login" && (
                <span className="absolute -top-2 right-3 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none" style={{ backgroundColor: "var(--login-card)", color: "var(--login-secondary)", border: "1px solid var(--login-border)" }}>
                  {t("lastUsed")}
                </span>
              )}
            </button>
          </form>

          {/* Toggle */}
          <p className="text-center text-xs" style={{ color: "var(--login-secondary)" }}>
            {mode === "login" ? t("noAccountPrompt") : t("hasAccountPrompt")}
            <span
              onClick={() => { resetForm(); setMode(mode === "login" ? "signup" : "login") }}
              className="font-semibold cursor-pointer hover:opacity-80 transition-opacity"
              style={{ color: "var(--login-signup-link)" }}
            >
              {mode === "login" ? t("signUpLink") : t("signInLink")}
            </span>
          </p>

          {/* Continue as guest */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-full h-10 rounded-lg text-xs font-medium transition-opacity hover:opacity-80"
            style={{ backgroundColor: "var(--login-guest-bg)", border: "1px solid var(--login-guest-border)", color: "var(--login-guest-text)" }}
          >
            {t("continueAsGuest")}
          </button>

        </div>
      </DialogContent>
    </Dialog>
  )
}
